import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ExtractedStorageResources, extractStorageResources } from '@campai/lib-common/entities'
import { useUpload } from '@campai/lib-web/components'
import { RichTextEditor } from '@campai/lib-web/components/common/RichTextEditor'
import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldLabel,
    Input,
} from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

import {
    AttachmentEditor,
    createAttachmentInput,
    createRichTextEditorResources,
    splitAttachments,
    type AttachmentInput,
} from '@/product/community/shared/Attachments'

type Topic = RouterOutput['product']['community']['topics']['getTopic']

type TopicDialogProps = {
    open: boolean
    topic?: Topic | null
    onOpenChange: (open: boolean) => void
    onSubmit: (input: { title: string; message: string; attachments: AttachmentInput }) => Promise<void>
}

export const TopicDialog = ({ open, topic, onOpenChange, onSubmit }: TopicDialogProps) => {
    const { t } = useTranslation()
    const [title, setTitle] = useState('')
    const [message, setMessage] = useState('')
    const [imageAttachments, setImageAttachments] = useState<ExtractedStorageResources>([])
    const [fileAttachments, setFileAttachments] = useState<ExtractedStorageResources>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editorSessionKey, setEditorSessionKey] = useState(0)
    const { upload, isUploading } = useUpload()

    useEffect(() => {
        if (!open) {
            return
        }

        setTitle(topic?.title || '')
        setMessage(topic?.message || '')
        const nextAttachments = topic ? extractStorageResources(topic.attachments) : []
        const { imageAttachments, fileAttachments } = splitAttachments(topic?.message || '', nextAttachments)
        setImageAttachments(imageAttachments)
        setFileAttachments(fileAttachments)
        setEditorSessionKey((currentKey) => currentKey + 1)
    }, [open, topic])

    const handleImageUpload = useCallback(
        async (file: File) => {
            const uploadResult = await upload(file)
            if (!uploadResult) {
                return null
            }

            const attachment = {
                resource: uploadResult.fileId,
                url: uploadResult.url,
                fileName: uploadResult.fileName,
                contentType: uploadResult.file.type || undefined,
                fileSizeBytes: uploadResult.file.size || undefined,
            }

            setImageAttachments((currentAttachments) => [...currentAttachments, attachment])

            return {
                id: uploadResult.fileId,
                url: uploadResult.url,
                alt: uploadResult.fileName,
            }
        },
        [upload]
    )

    const handleSubmit = async () => {
        if (!title.trim() || !message.trim() || isSubmitting || isUploading) {
            return
        }

        setIsSubmitting(true)

        try {
            await onSubmit({
                title: title.trim(),
                message: message.trim(),
                attachments: createAttachmentInput({
                    markdown: message,
                    imageAttachments,
                    fileAttachments,
                }),
            })
            onOpenChange(false)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[min(52rem,calc(100vh-2rem))] min-h-[min(42rem,calc(100vh-2rem))] grid-cols-none flex-col gap-4 sm:max-w-4xl">
                <DialogHeader className="shrink-0">
                    <DialogTitle>
                        {topic
                            ? t('product.community.shared.topicDialog.titleEdit')
                            : t('product.community.shared.topicDialog.titleCreate')}
                    </DialogTitle>
                    <DialogDescription>{t('product.community.shared.topicDialog.description')}</DialogDescription>
                </DialogHeader>
                <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1">
                    <Field className="shrink-0">
                        <FieldLabel required>{t('product.community.shared.topicDialog.topicTitle')}</FieldLabel>
                        <Input
                            value={title}
                            maxLength={120}
                            onChange={(event) => setTitle(event.currentTarget.value)}
                        />
                    </Field>
                    <Field className="min-h-0 flex-1">
                        <FieldLabel required>{t('product.community.shared.topicDialog.message')}</FieldLabel>
                        <RichTextEditor
                            key={editorSessionKey}
                            className="min-h-0 flex-1"
                            editorClassName="flex min-h-0 flex-1 flex-col"
                            value={message}
                            onChange={setMessage}
                            maxLength={10000}
                            minRows={0}
                            maxRows={0}
                            syntaxHighlighting
                            features={{ headings: true, quote: true, codeBlock: true, taskList: true }}
                            imageUpload={handleImageUpload}
                            resources={createRichTextEditorResources(imageAttachments)}
                        />
                    </Field>
                    <Field>
                        <FieldLabel>{t('product.community.shared.attachments.title')}</FieldLabel>
                        <AttachmentEditor
                            attachments={fileAttachments}
                            disabled={isSubmitting || isUploading}
                            upload={upload}
                            onChange={setFileAttachments}
                        />
                    </Field>
                </div>
                <DialogFooter className="shrink-0">
                    <DialogClose
                        render={
                            <Button variant="outline" disabled={isSubmitting || isUploading}>
                                {t('common.cancel')}
                            </Button>
                        }
                    />
                    <Button
                        loading={isSubmitting || isUploading}
                        disabled={!title.trim() || !message.trim()}
                        onClick={() => void handleSubmit()}
                    >
                        {topic ? t('common.save') : t('product.community.shared.topicDialog.create')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
